import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'

import isEqual from 'fast-deep-equal'

import { auth, removeRegisteredUser, startAccessTokenRefreshTimer } from '../actions/auth'
import { getAccessToken } from '../actions/token'
import { getActiveUser, hasRegisteredUser, updateRegisteredUser } from '../actions/user'
import { authUser } from '../constants/common'
import { isDevMode } from '../envs/envs'
import { useEventListener } from '../hooks/useEventListener'
import { Auth } from '../service/auth'
import { AccessRights, AuthType } from '../types/authTypes'
import { UserResponseDataType } from '../types/servicesTypes/identityTypes'
import { setupLocalStorageListener } from '../utils/utils'

export type AuthContextProps = {
  isAuthenticatedUserType: Nullable<AuthType>
  isAnonymousUser: boolean
  isRegisteredUser: boolean
  accessRights: Nullable<AccessRights>
  auth: () => void
  updateAccessRights: () => void
  removeRegisteredUser: () => void
  setIsAuthenticatedUserType: (data: AuthType) => void
  setRegisteredUser: (data: UserResponseDataType) => void
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined)

export const AuthProvider = ({ children }: PropsWithChildren<Record<string, unknown>>) => {
  const [isAuthenticatedUserType, setIsAuthenticatedUserType] = useState<Nullable<AuthType>>(null)
  const [accessRights, setAccessRights] = useState<AccessRights>(null)

  const isAnonymousUser = isAuthenticatedUserType === authUser.ANONYMOUS
  const isRegisteredUser = isAuthenticatedUserType === authUser.REGISTERED

  const localStorageUserTypeListener = useCallback(({ key }: StorageEvent) => {
    if (Object.values(authUser).includes(key as AuthType) && isAuthenticatedUserType !== key) {
      isDevMode && console.log(`[sdk authContext] localStorageUserTypeListener:  ${key}`)
      const token = getAccessToken()
      const user = getActiveUser()
      if (token) {
        setIsAuthenticatedUserType(key as AuthType)
      }
      if (user) {
        startAccessTokenRefreshTimer(user.accessTokenExpiryTime)
      }
    }
  }, [])

  useEventListener('storage', localStorageUserTypeListener)

  useEffect(() => {
    if (!isAuthenticatedUserType) {
      return
    }

    const token = getAccessToken()
    if (!token) {
      isDevMode && console.log('[sdk authContext] token not found')
      return
    }

    void updateAccessRights()
  }, [isAuthenticatedUserType])

  useEffect(() => {
    setupLocalStorageListener()
    void auth()
  }, [])

  /**
   * Updates the access rights based on the authenticated user type and registered user status.
   *
   * @return {Promise<void>} - A promise that resolves when the access rights are updated.
   */
  const updateAccessRights = async () => {
    if (isAuthenticatedUserType && hasRegisteredUser()) {
      try {
        const data = await Auth.getAccessRights()
        if (data) {
          isDevMode && console.log(`[sdk 🔑 updateAccessRights] success`, data.customerId)
          const isAccessRightsChanged = !isEqual(accessRights, data)
          isAccessRightsChanged && setAccessRights(data)
        }
      } catch (error) {
        isDevMode && console.log(`[sdk 🔑 updateAccessRights]: server error`)
      }
    } else {
      setAccessRights(null)
      isDevMode && console.log(`[sdk 🔑 updateAccessRights]: set null`)
    }
  }

  const context = {
    isAuthenticatedUserType,
    isAnonymousUser,
    isRegisteredUser,
    accessRights,
    auth,
    updateAccessRights,
    removeRegisteredUser,
    setRegisteredUser: updateRegisteredUser,
    setIsAuthenticatedUserType,
  }

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
}

/**
 * Returns the authentication context props.
 *
 * @return {AuthContextProps} The authentication context props.
 * @throws {Error} If useAuth is not used within an AuthProvider.
 */
export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
